import { appApi, idTag, orgTag } from '@/app/redux/appApi';
import { GameSettings, LevelProgress, Student } from '@/common/types/student';
import { getUpdatedStudent } from '@/common/utils/student';
import api from '@/db/apiClient';

export const studentsApi = appApi.injectEndpoints({
  endpoints: (build) => ({
    getStudentsForOrganisation: build.query<Student[], string>({
      query: async (orgId) => {
        const rawData = await api.asEducator.getStudentsForOrganisation(orgId);
        return Promise.all(rawData.map((s) => getUpdatedStudent(s)));
      },
      providesTags: (result, _err, orgId) =>
        result
          ? [
              orgTag('Students', orgId),
              ...result.map((s) => idTag('Students', s.id)),
            ]
          : [orgTag('Students', orgId)],
    }),

    createStudent: build.mutation<Student, Omit<Student, 'id'>>({
      query: async (student) => {
        const id = await api.asEducator.createNewStudent(student);
        return { id, ...student };
      },
      invalidatesTags: (_res, _err, student) => [
        orgTag('Students', student.organisationId),
      ],
    }),

    bulkCreateStudents: build.mutation<
      string[],
      { organisationId: string; students: Omit<Student, 'id'>[] }
    >({
      query: async (params) => {
        const { studentIds } = await api.asEducator.bulkAddStudents(params);
        return studentIds;
      },
      invalidatesTags: (_res, _err, { organisationId }) => [
        orgTag('Students', organisationId),
      ],
    }),

    updateStudent: build.mutation<
      null,
      { orgId: string; studentId: string; updates: Partial<Student> }
    >({
      query: async ({ studentId, updates }) => {
        await api.asEducator.updateStudentDetails(studentId, updates);
        return null;
      },
      onQueryStarted: async (
        { orgId, studentId, updates },
        { dispatch, queryFulfilled },
      ) => {
        // Patch the org list cache
        const patch = dispatch(
          studentsApi.util.updateQueryData(
            'getStudentsForOrganisation',
            orgId,
            (draft) => {
              const s = draft.find((x) => x.id === studentId);
              if (s) Object.assign(s, updates, { updatedAt: Date.now() });
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),

    bulkAssignStudentAssessments: build.mutation<
      null,
      { studentIds: string[]; assessmentId: string; organisationId: string }
    >({
      query: async ({ studentIds, assessmentId }) => {
        await api.asEducator.bulkAssignStudentAssessments({
          studentIds,
          assessmentId,
        });
        return null;
      },
      onQueryStarted: async (
        { organisationId, studentIds, assessmentId },
        { dispatch, queryFulfilled },
      ) => {
        const patch = dispatch(
          studentsApi.util.updateQueryData(
            'getStudentsForOrganisation',
            organisationId,
            (draft) => {
              const idSet = new Set(studentIds);
              for (const s of draft) {
                if (idSet.has(s.id)) s.assessmentId = assessmentId;
              }
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),

    bulkUpdateStudentSettings: build.mutation<
      null,
      {
        studentIds: string[];
        userName: string;
        organisationId: string;
        settings: GameSettings;
        location: string;
      }
    >({
      query: async (params) => {
        await api.asEducator.bulkUpdateStudentSettings(params);
        return null;
      },
      onQueryStarted: async (
        { organisationId, studentIds, settings },
        { dispatch, queryFulfilled },
      ) => {
        const patch = dispatch(
          studentsApi.util.updateQueryData(
            'getStudentsForOrganisation',
            organisationId,
            (draft) => {
              const idSet = new Set(studentIds);
              for (const s of draft) {
                if (idSet.has(s.id)) s.settings = settings;
              }
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),

    deleteStudent: build.mutation<
      { deletedAssessmentIds: string[] },
      { orgId: string; studentId: string }
    >({
      query: ({ orgId, studentId }) =>
        api.asEducator.deleteStudent(studentId, orgId),
      onQueryStarted: async (
        { orgId, studentId },
        { dispatch, queryFulfilled },
      ) => {
        // Patch the org list cache
        const patch = dispatch(
          studentsApi.util.updateQueryData(
            'getStudentsForOrganisation',
            orgId,
            (draft) => {
              const idx = draft.findIndex((s) => s.id === studentId);
              if (idx !== -1) draft.splice(idx, 1);
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),

    bulkDeleteStudents: build.mutation<
      { deletedAssessmentIds: string[] },
      { orgId: string; studentIds: string[] }
    >({
      query: ({ studentIds }) =>
        api.asEducator.bulkDeleteStudents({ studentIds }),
      onQueryStarted: async (
        { orgId, studentIds },
        { dispatch, queryFulfilled },
      ) => {
        // Patch the org list cache
        const patch = dispatch(
          studentsApi.util.updateQueryData(
            'getStudentsForOrganisation',
            orgId,
            (draft) => {
              const idsToRemove = new Set(studentIds);
              return draft.filter((s) => !idsToRemove.has(s.id));
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),

    bulkUpdateStudents: build.mutation<
      null,
      {
        organisationId: string;
        updates: Array<{
          studentId: string;
          password?: string;
          spellcastUsername?: string;
          level?: number;
          levelsProgress?: LevelProgress[];
        }>;
      }
    >({
      query: async (params) => {
        await api.asEducator.bulkUpdateStudents(params);
        return null;
      },
      onQueryStarted: async (
        { organisationId, updates },
        { dispatch, queryFulfilled },
      ) => {
        const patch = dispatch(
          studentsApi.util.updateQueryData(
            'getStudentsForOrganisation',
            organisationId,
            (draft) => {
              for (const { studentId, ...fields } of updates) {
                const s = draft.find((x) => x.id === studentId);
                if (s) Object.assign(s, fields, { updatedAt: Date.now() });
              }
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),
  }),

  overrideExisting: false,
});

export default studentsApi;
export const {
  useGetStudentsForOrganisationQuery,
  useCreateStudentMutation,
  useUpdateStudentMutation,
  useBulkCreateStudentsMutation,
  useBulkAssignStudentAssessmentsMutation,
  useBulkUpdateStudentSettingsMutation,
  useDeleteStudentMutation,
  useBulkDeleteStudentsMutation,
  useBulkUpdateStudentsMutation,
} = studentsApi;
